import { useEffect, useMemo, useState } from 'react';
import { CaretIcon, PlusIcon, TrashIcon, EditLineIcon } from '../../../assets/icons';
import AddLevelModal, { type NewLevel, type ParentOption } from './AddLevelModal';
import { SelectedHierarchy } from './BuildHierarchyStep';
import type { HierarchyView } from './DefineBoundaryStep';


/* ------------------------------------------------------------------ *
 * Right panel — the editable administrative-level hierarchy.
 * ------------------------------------------------------------------ */
export interface AdminLevel {
  id: string;
  name: string;
  parentId: string | null;
}

let levelSeq = 0;
const nextLevelId = () => `al-${(levelSeq += 1)}`;

interface TreeHandlers {
  onRemove: (id: string) => void;
  editingId: string | null;
  draft: string;
  onDraft: (v: string) => void;
  onStartEdit: (id: string, name: string) => void;
  onCommitEdit: () => void;
  onCancelEdit: () => void;
}

function AdminLevelTree({
  levels,
  parentId,
  depth,
  handlers,
}: {
  levels: AdminLevel[];
  parentId: string | null;
  depth: number;
  handlers: TreeHandlers;
}) {
  const kids = levels.filter((l) => l.parentId === parentId);
  if (kids.length === 0) return null;
  const { onRemove, editingId, draft, onDraft, onStartEdit, onCommitEdit, onCancelEdit } = handlers;
  return (
    <div className={depth === 0 ? 'flex flex-col gap-1' : 'ml-3 flex flex-col gap-1 border-l border-[#e2e8f0] pl-3'}>
      {kids.map((l) => (
        <div key={l.id}>
          <div className="group flex items-center gap-2 rounded-lg py-1.5">
            <CaretIcon className="size-3.5 shrink-0 text-[#94a3b8]" />
            {editingId === l.id ? (
              <input
                autoFocus
                value={draft}
                onChange={(e) => onDraft(e.target.value)}
                onBlur={onCommitEdit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onCommitEdit();
                  if (e.key === 'Escape') onCancelEdit();
                }}
                className="h-8 flex-1 rounded-md border border-[#cbd5e1] bg-white px-2 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
              />
            ) : (
              <span
                className={[
                  'flex-1 truncate text-sm',
                  depth === 0 ? 'font-semibold text-ink' : 'text-ink-secondary',
                ].join(' ')}
              >
                {l.name}
              </span>
            )}
            <div className="flex shrink-0 items-center gap-2 text-ink-secondary opacity-0 transition-opacity group-hover:opacity-100">
              <button
                type="button"
                aria-label={`Edit ${l.name}`}
                onClick={() => onStartEdit(l.id, l.name)}
                className="transition-colors hover:text-brand"
              >
                <EditLineIcon className="size-3.5" />
              </button>
              <button
                type="button"
                aria-label={`Remove ${l.name}`}
                onClick={() => onRemove(l.id)}
                className="transition-colors hover:text-red-500"
              >
                <TrashIcon className="size-3.5" />
              </button>
            </div>
          </div>
          <AdminLevelTree levels={levels} parentId={l.id} depth={depth + 1} handlers={handlers} />
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Step 5 — Build Administrative Hierarchy
 * ------------------------------------------------------------------ */
const PANEL = 'flex flex-col gap-5 rounded-xl border border-[#e5e7eb] bg-white p-6 shadow-card';

export default function AdminHierarchyStep({
  selection,
  initialLevels,
  onLevelsChange,
}: {
  /** The selected constituency from step 2 (shown read-only, same as step 3). */
  selection?: HierarchyView[];
  /** Saved admin levels (edit mode) — overrides the seed defaults when non-empty. */
  initialLevels?: AdminLevel[];
  /** Notifies the wizard of the current admin-level list (for persistence). */
  onLevelsChange?: (levels: AdminLevel[]) => void;
}) {
  const [levels, setLevels] = useState<AdminLevel[]>(initialLevels ?? []);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  // Emit the current levels on mount so a Save with no edits still persists them.
  useEffect(() => {
    onLevelsChange?.(levels);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When saved levels arrive (edit mode, loaded async), replace the seed defaults.
  useEffect(() => {
    if (initialLevels && initialLevels.length > 0) {
      setLevels(initialLevels);
      onLevelsChange?.(initialLevels);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLevels]);

  const update = (next: AdminLevel[]) => {
    setLevels(next);
    onLevelsChange?.(next);
  };

  // Flatten levels into indented parent options for the Add-Level modal.
  const parentOptions = useMemo<ParentOption[]>(() => {
    const out: ParentOption[] = [];
    const walk = (parentId: string | null, depth: number) => {
      levels
        .filter((l) => l.parentId === parentId)
        .forEach((l) => {
          out.push({ id: l.id, name: l.name, depth });
          walk(l.id, depth + 1);
        });
    };
    walk(null, 0);
    return out;
  }, [levels]);

  const addLevel = (level: NewLevel) => {
    const id = nextLevelId();
    const target = level.parent ? levels.find((l) => l.id === level.parent) : undefined;
    if (level.placement === 'parent' && target) {
      // Parent: the new node becomes the parent of the selected node.
      update([
        ...levels.map((l) => (l.id === target.id ? { ...l, parentId: id } : l)),
        { id, name: level.name, parentId: target.parentId },
      ]);
    } else if (level.placement === 'sibling') {
      // Sibling: same level as the selected node, sharing its parent.
      update([...levels, { id, name: level.name, parentId: target ? target.parentId : null }]);
    } else {
      // Child (default): nests under the selected node (root if none selected).
      update([...levels, { id, name: level.name, parentId: target ? target.id : null }]);
    }
    setModalOpen(false);
  };

  const removeLevel = (id: string) => {
    // Drop the node and any descendants.
    const doomed = new Set<string>([id]);
    let changed = true;
    while (changed) {
      changed = false;
      levels.forEach((l) => {
        if (l.parentId && doomed.has(l.parentId) && !doomed.has(l.id)) {
          doomed.add(l.id);
          changed = true;
        }
      });
    }
    update(levels.filter((l) => !doomed.has(l.id)));
    if (editingId === id) setEditingId(null);
  };

  const startEdit = (id: string, name: string) => {
    setEditingId(id);
    setDraft(name);
  };
  const commitEdit = () => {
    if (!editingId) return;
    const name = draft.trim();
    if (name) update(levels.map((l) => (l.id === editingId ? { ...l, name } : l)));
    setEditingId(null);
  };
  const treeHandlers: TreeHandlers = {
    onRemove: removeLevel,
    editingId,
    draft,
    onDraft: setDraft,
    onStartEdit: startEdit,
    onCommitEdit: commitEdit,
    onCancelEdit: () => setEditingId(null),
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Left — selected constituency hierarchy (read-only, same as step 3) */}
        <div className={PANEL}>
          <h2 className="text-lg font-bold text-ink">Selected Constituency Hierarchy</h2>
          <SelectedHierarchy nodes={selection ?? []} />
        </div>

        {/* Right — administrative level hierarchy (editable) */}
        <div className={PANEL}>
          <div className="flex items-start justify-between gap-3">
            <h2 className="text-lg font-bold text-ink">Administrative Level Hierarchy</h2>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="flex shrink-0 items-center gap-1 rounded-md border border-brand px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.5px] text-brand transition-colors hover:bg-brand-shade"
            >
              <PlusIcon className="size-3" />
              Add New Level
            </button>
          </div>
          {levels.length === 0 ? (
            <p className="py-8 text-center text-sm text-ink-secondary">
              No administrative levels yet. Use “Add New Level” to build the hierarchy.
            </p>
          ) : (
            <AdminLevelTree levels={levels} parentId={null} depth={0} handlers={treeHandlers} />
          )}
        </div>
      </div>

      <AddLevelModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreate={addLevel}
        parents={parentOptions}
      />
    </div>
  );
}
